import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "../services/translation.service";
import { useCart } from "../context/CartContext";

type LanguageSwitcherProps = {
  variant?: "header" | "compact";
  className?: string;
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = "header",
  className = "",
}) => {
  const { changeLanguage, getAvailableLanguages, currentLanguage } =
    useTranslation();
  const { businessInfo } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const availableLanguages = getAvailableLanguages(businessInfo?.countryID);
  const activeLanguage = availableLanguages.find(
    (language) => language.code === currentLanguage,
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const triggerClassName =
    variant === "compact"
      ? "flex h-8 items-center gap-1 bg-transparent px-1 transition-opacity hover:opacity-70"
      : "flex h-9 items-center gap-1 rounded-lg bg-transparent px-1 text-gray-700 transition-colors hover:text-[#dc2626] dark:text-gray-200";

  const menuClassName =
    variant === "compact"
      ? "absolute top-full end-0 z-20 mb-2 w-36 border border-gray-200 bg-white shadow-lg"
      : "absolute end-0 z-[100] mt-2 w-40 border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800 sm:w-48";

  return (
    <motion.div
      ref={rootRef}
      className={`relative ${className}`}
      whileTap={variant === "header" ? { scale: 0.95 } : undefined}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        className={triggerClassName}
        aria-label={activeLanguage?.name}
        aria-expanded={isOpen}
      >
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown
            className={
              variant === "compact" ? "h-3 w-3 opacity-70" : "h-3 w-3"
            }
          />
        </motion.div>
        <span
          className={
            variant === "compact"
              ? "text-xs font-medium"
              : "text-sm font-medium"
          }
        >
          {activeLanguage?.name}
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              y: variant === "compact" ? 6 : -10,
              scale: 0.95,
            }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: variant === "compact" ? 6 : -10,
              scale: 0.95,
            }}
            transition={{ duration: 0.2 }}
            className={menuClassName}
            style={{ color: "#1f2937" }}
            onClick={(event) => event.stopPropagation()}
          >
            <motion.div>
              {availableLanguages.map((language) => {
                const isActive = currentLanguage === language.code;
                return (
                  <button
                    key={language.code}
                    type="button"
                    onClick={() => {
                      changeLanguage(language.code);
                      setIsOpen(false);
                    }}
                    style={{ color: isActive ? "#b91c1c" : "#1f2937" }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-red-50 sm:px-4 sm:py-3 ${
                      isActive ? "bg-red-50" : ""
                    }`}
                  >
                    <span style={{ color: isActive ? "#b91c1c" : "#1f2937" }}>
                      {language.name}
                    </span>
                    {isActive && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="ms-auto h-1.5 w-1.5 rounded-full bg-red-600 sm:h-2 sm:w-2"
                      />
                    )}
                  </button>
                );
              })}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LanguageSwitcher;
